/**
 * Pure observation-note types and constants — no relative imports, so this
 * module is safe to import from both the server (NodeNext) and the client
 * (Vite). The DB-backed query/mutation logic lives in the observations router.
 */

export const OBSERVATION_TAGS = ["win", "obstacle", "risk", "na"] as const;
export type ObservationTag = (typeof OBSERVATION_TAGS)[number];

export function isObservationTag(value: unknown): value is ObservationTag {
  return (
    typeof value === "string" &&
    (OBSERVATION_TAGS as readonly string[]).includes(value)
  );
}

export const OBSERVATION_IMAGE_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;
export type ObservationImageContentType =
  (typeof OBSERVATION_IMAGE_CONTENT_TYPES)[number];

export const OBSERVATION_IMAGE_MAX_BYTE_SIZE = 5 * 1024 * 1024;
export const OBSERVATION_IMAGE_MAX_COUNT = 10;

export function isObservationImageContentType(
  value: unknown,
): value is ObservationImageContentType {
  return (
    typeof value === "string" &&
    (OBSERVATION_IMAGE_CONTENT_TYPES as readonly string[]).includes(value)
  );
}

export interface ObservationImageData {
  id: string;
  contentType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  createdAt: string;
  url: string;
}

/** A single observation note, projected for the UI (author name resolved). */
export interface ObservationNoteData {
  id: string;
  tag: ObservationTag;
  title: string | null;
  body: string;
  observationDate: string;
  createdAt: string;
  updatedAt: string;
  authorName: string;
  images: ObservationImageData[];
}

export interface CreateObservationInput {
  rdId: string;
  authorUserId: string;
  tag: ObservationTag;
  title?: string | null;
  body: string;
  /** ISO date (YYYY-MM-DD). Defaults to today when omitted. */
  observationDate?: string | null;
}

export interface UpdateObservationInput {
  tag?: ObservationTag;
  title?: string | null;
  body?: string;
  observationDate?: string;
}
